// src/modules/user/UserList.tsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUsers, User, UsersPaginatedResponse } from "./userApi";
import styles from "./UserShared.module.css";
import {
  Users,
  Eye,
  Search,
  UserCheck,
  UserX,
  Shield,
  Calendar,
  BarChart3,
  AlertCircle,
  RefreshCw,
  Globe,
  Trophy,
} from "lucide-react";

export default function UserList() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadUsers(currentPage);
  }, [currentPage]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;

    const lowercaseSearch = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowercaseSearch) ||
        user.email?.toLowerCase().includes(lowercaseSearch) ||
        user.user_id?.toLowerCase().includes(lowercaseSearch)
    );
  }, [users, searchTerm]);

  const loadUsers = async (page: number = 1) => {
    try {
      setLoading(true);
      setError("");
      const response: UsersPaginatedResponse = await fetchUsers(page);
      console.log("Users loaded:", response);

      // API response flat structure - direkt erişim (gerçek API)
      setUsers(response.users || []);
      setCurrentPage(response.page);
      setPageSize(response.page_size);

      // Backend'den total_pages gelmiyor, sayfa sayısını hesaplıyoruz
      if (response.users.length === 0 && page > 1) {
        // Eğer bu sayfada hiç veri yoksa, önceki sayfa son sayfaydı
        setTotalPages(page - 1);
        setCurrentPage(page - 1);
      } else if (response.users.length < response.page_size) {
        // Bu sayfada veri var ama sayfa boyutundan az ise, bu son sayfa
        setTotalPages(page);
      } else {
        // Bu sayfa dolu, muhtemelen daha fazla sayfa var
        setTotalPages(page + 1); // En az bir sonraki sayfa var gibi göster
      }
    } catch (error: any) {
      console.error("Failed to load users:", error);
      setError(
        error.response?.data?.message || "Kullanıcılar yüklenirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (user_id: string, name: string) => {
    // Backend'de kullanıcı silme endpoint'i henüz mevcut değil
    alert(
      "Kullanıcı silme özelliği henüz mevcut değil. Backend'de endpoint bulunmuyor."
    );
  };

  const formatLastLogin = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Bugün";
    if (diffDays === 2) return "Dün";
    if (diffDays <= 7) return `${diffDays} gün önce`;

    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getActivityStatus = (lastLogin: string) => {
    const date = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.ceil(
      Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays <= 7) return { color: "#10b981", label: "Aktif" };
    if (diffDays <= 30) return { color: "#f59e0b", label: "Pasif" };
    return { color: "#ef4444", label: "İnaktif" };
  };

  const getStats = () => {
    const activeUsers = users.filter(
      (u) => getActivityStatus(u.last_login).color === "#10b981"
    ).length;
    const totalDevices = users.reduce(
      (acc, user) => acc + user.devices.length,
      0
    );
    const newUsersThisWeek = users.filter((user) => {
      const createdDate = new Date(user.created_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return createdDate > weekAgo;
    }).length;

    return {
      totalUsers: users.length,
      activeUsers,
      totalDevices,
      newUsersThisWeek,
    };
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>Kullanıcılar yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div
          style={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            border: "1px solid #fecaca",
            borderRadius: "0.5rem",
            padding: "1rem",
            color: "#dc2626",
            textAlign: "center" as const,
          }}
        >
          <h3>Hata</h3>
          <p>{error}</p>
          <button
            onClick={() => loadUsers(currentPage)}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              background: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "0.25rem",
              cursor: "pointer",
            }}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div className={styles.container}>
      {/* Modern Header with Gradient */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <Users size={28} />
            </div>
            <div>
              <h1 className={styles.title}>Kullanıcı Yönetimi</h1>
              <p className={styles.subtitle}>
                Tüm kullanıcıları görüntüleyin ve yönetin
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button
              onClick={() => loadUsers(currentPage)}
              className={styles.refreshButton}
              title="Yenile"
            >
              <RefreshCw size={18} />
              Yenile
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <Users size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.totalUsers}</div>
            <div className={styles.statLabel}>Toplam Kullanıcı</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            <UserCheck size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.activeUsers}</div>
            <div className={styles.statLabel}>Aktif Kullanıcı</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
            }}
          >
            <Shield size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.totalDevices}</div>
            <div className={styles.statLabel}>Toplam Cihaz</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div
            className={styles.statIcon}
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          >
            <Calendar size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{stats.newUsersThisWeek}</div>
            <div className={styles.statLabel}>Bu Hafta Yeni</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <Search size={20} />
          <input
            type="text"
            placeholder="Kullanıcı ara... (isim, email, ID)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        searchTerm ? (
          <div className={styles.emptyState}>
            <AlertCircle size={48} />
            <h3>Arama sonucu bulunamadı</h3>
            <p>"{searchTerm}" için herhangi bir kullanıcı bulunamadı.</p>
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Users size={48} />
            <h3>Henüz kullanıcı yok</h3>
            <p>Kullanıcılar uygulamaya kayıt oldukça burada görünecek.</p>
          </div>
        )
      ) : (
        <>
          {/* Compact Grid Layout */}
          <div className={styles.compactGridContainer}>
            {filteredUsers.map((user) => {
              const activityStatus = getActivityStatus(user.last_login);
              return (
                <div key={user.user_id} className={styles.compactCard}>
                  <div className={styles.compactCardHeader}>
                    <div className={styles.userAvatar}>
                      {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className={styles.userInfo}>
                      <h3 className={styles.userName}>
                        {user.name || "İsimsiz"}
                      </h3>
                      <p className={styles.userEmail}>{user.email}</p>
                    </div>
                    <div
                      className={styles.statusBadge}
                      style={{ backgroundColor: activityStatus.color }}
                    >
                      {activityStatus.label}
                    </div>
                  </div>

                  <div className={styles.compactCardContent}>
                    <div className={styles.compactInfoGrid}>
                      <div className={styles.compactInfoItem}>
                        <Calendar size={14} />
                        <span>{formatLastLogin(user.last_login)}</span>
                      </div>

                      <div className={styles.compactInfoItem}>
                        <Shield size={14} />
                        <span>{user.devices.length} cihaz</span>
                      </div>

                      <div className={styles.compactInfoItem}>
                        <BarChart3 size={14} />
                        <span>{user.assigned_song_ids.length} şarkı</span>
                      </div>

                      <div className={styles.compactInfoItem}>
                        <Globe size={14} />
                        <span>{user.language || "english"}</span>
                      </div>

                      <div className={styles.compactInfoItem}>
                        <Trophy size={14} />
                        <span>{user.score ?? 0} puan</span>
                      </div>
                    </div>

                    <div className={styles.compactCardActions}>
                      <button
                        onClick={() =>
                          navigate(`/users/${user.user_id}`, {
                            state: { user },
                          })
                        }
                        className={styles.compactPrimaryButton}
                        title="Detayları Görüntüle"
                      >
                        <Eye size={14} />
                      </button>

                      <button
                        onClick={() =>
                          handleDelete(user.user_id, user.name || "İsimsiz")
                        }
                        className={styles.compactDangerButton}
                        title="Kullanıcıyı Sil"
                      >
                        <UserX size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modern Pagination */}
          {(currentPage > 1 || users.length === pageSize) && (
            <div className={styles.paginationContainer}>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className={`${styles.paginationButton} ${
                  currentPage <= 1 ? styles.disabled : ""
                }`}
              >
                Önceki Sayfa
              </button>

              <div className={styles.paginationInfo}>
                <span>Sayfa {currentPage}</span>
                {users.length < pageSize && (
                  <span className={styles.lastPageIndicator}>Son sayfa</span>
                )}
              </div>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={users.length < pageSize}
                className={`${styles.paginationButton} ${
                  users.length < pageSize ? styles.disabled : ""
                }`}
              >
                Sonraki Sayfa
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
